import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { useProducts } from "../hooks/useProducts";
import { ChevronDown, Loader2 } from "lucide-react";

const CATS = [
  { key: "women", label: "Women" },
  { key: "men", label: "Men" },
  { key: "accessories", label: "Scarves & Bags" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "women";
  const [sort, setSort] = useState("featured");
  const { products, loading, error } = useProducts();

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.category === cat);
    if (sort === "low") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "high") list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [cat, sort, products]);

  const setCat = (k) => setParams({ cat: k });

  const title = CATS.find((c) => c.key === cat)?.label || "Shop";

  return (
    <main className="max-w-[1400px] mx-auto px-5 md:px-10 pt-10 md:pt-14">
      <header className="text-center mb-10 md:mb-14">
        <span className="text-[11px] tracking-[0.32em] uppercase text-[hsl(var(--kq-accent-2))]">
          — The Collection
        </span>
        <h1 className="font-display text-5xl md:text-6xl mt-3 leading-none">{title}</h1>
        <div className="mt-4 flex items-center justify-center"><span className="kq-thin-rule" /></div>
        <p className="font-italic text-base md:text-lg text-[hsl(var(--kq-ink-soft))] mt-5 max-w-xl mx-auto">
          Hand-loomed and block-printed in Northern India. Every piece finished by hand.
        </p>
      </header>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10 pb-5 border-b border-[hsl(var(--kq-line))]">
        <nav className="flex flex-wrap gap-1.5">
          {CATS.map((c) => (
            <button
              key={c.key}
              onClick={() => setCat(c.key)}
              data-testid={`shop-cat-${c.key}`}
              className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                cat === c.key
                  ? "bg-[hsl(var(--kq-ink))] text-[hsl(var(--kq-bg))] border-[hsl(var(--kq-ink))]"
                  : "border-[hsl(var(--kq-line))] hover:border-[hsl(var(--kq-ink))]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-[hsl(var(--kq-ink-soft))]" data-testid="shop-count">{filtered.length} pieces</span>
          <span className="text-[hsl(var(--kq-line))]">|</span>
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="shop-sort"
              className="appearance-none bg-transparent pr-7 pl-3 py-2 text-[11px] tracking-[0.2em] uppercase outline-none border border-[hsl(var(--kq-line))]"
            >
              <option value="featured">Featured</option>
              <option value="low">Price: Low → High</option>
              <option value="high">Price: High → Low</option>
            </select>
            <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>

      {loading && (
        <div className="py-32 flex flex-col items-center text-[hsl(var(--kq-ink-soft))]" data-testid="shop-loading">
          <Loader2 className="w-6 h-6 animate-spin mb-3" />
          <p className="text-xs tracking-[0.2em] uppercase">Loading collection…</p>
        </div>
      )}

      {error && !loading && (
        <div className="py-32 text-center" data-testid="shop-error">
          <p className="text-sm text-[hsl(var(--kq-ink-soft))]">We couldn't load the collection right now. Please try again shortly.</p>
        </div>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-12 md:gap-x-7 md:gap-y-16" data-testid="shop-grid">
          {filtered.map((p, i) => (
            // First 4 cards (the lg desktop row above the fold) load eagerly +
            // with high priority so they arrive ~100-200ms sooner.
            <ProductCard key={p.id} product={p} priority={i < 4} />
          ))}
        </div>
      )}
    </main>
  );
}
